import { Auth } from "../models/Auth";
import { User } from "../models/User";
import ApiPaths from "./ApiPaths";
import { httpService } from "./HttpService";
import axios from 'axios';

type ApiLoginResponse = User & {
  auth: Auth;
};

export const login = async (email: string, password: string, scaCode?: string): Promise<{ auth: Auth, user: User }> => {
    try {
        const response = await httpService.post<ApiLoginResponse>(ApiPaths.AUTH_LOGIN, { email, password, scaCode });
        const { auth, ...user } = response.data;
        return { auth, user };
    } catch (error) {
        if (axios.isAxiosError(error)) {
            if (error.code === 'ECONNABORTED') {
                throw new Error('timeout');
            }

            if (error.response?.status === 401 && error.response.data?.detail === 'SCA_IS_REQUIRED') {
                throw new Error('sca_is_required');
            }

            if (error.response && error.response.status >= 400 && error.response.status < 500) {
                throw new Error('invalid_credentials');
            }
        }

        throw error;
    }
}

export const logout = async (): Promise<void> => {
    try {
        await httpService.post<void>(ApiPaths.AUTH_LOGOUT, {});
    } catch (error) {
        console.error('Sign out failed:', error);
    }
}

export const verifyPassword = async (password: string): Promise<void> => {
    try {
        await httpService.post(ApiPaths.AUTH_VERIFY_PASSWORD, { password });
    } catch (error) {
        if (axios.isAxiosError(error) && error.response?.data?.detail) {
            throw new Error(error.response.data.detail);
        }
        throw new Error('Password is incorrect.');
    }
};

export const changePassword = async (currentPassword: string, newPassword: string): Promise<void> => {
    try {
        await httpService.post(ApiPaths.AUTH_CHANGE_PASSWORD, { currentPassword, newPassword });
    } catch (error) {
        if (axios.isAxiosError(error) && error.response?.data?.detail) {
            throw new Error(error.response.data.detail);
        }
        throw new Error('Failed to change password. Please try again.');
    }
};

export const forgotPassword = async (email: string): Promise<void> => {
    try {
        await httpService.post(ApiPaths.AUTH_FORGOT_PASSWORD, { email });
    } catch (error) {
        throw new Error('Failed to send reset email. Please try again.');
    }
};

export const validateResetToken = async (token: string): Promise<{ type: string }> => {
    try {
        const response = await httpService.get<{ type: string }>(`${ApiPaths.AUTH_VALIDATE_RESET_TOKEN}?token=${encodeURIComponent(token)}`);
        return response.data;
    } catch (error) {
        throw new Error('Invalid or expired reset link.');
    }
};

export const resetPassword = async (token: string, newPassword: string): Promise<void> => {
    try {
        await httpService.post(ApiPaths.AUTH_RESET_PASSWORD, { token, newPassword });
    } catch (error) {
        if (axios.isAxiosError(error) && error.response?.data?.detail) {
            throw new Error(error.response.data.detail);
        }
        throw new Error('Failed to reset password. Please try again.');
    }
};
